import Document from '@/profile/your-documents/entities/document.entity';
import DocumentType from '@/profile/your-documents/entities/document-type.entity';
import DocumentMetadata from '@/profile/your-documents/entities/document-metadata.entity';
import UploadMetadata from '@/profile/your-documents/entities/upload-metadata.entity';
import BackofficeRepository from '@/_shared/repositories/backoffice.repository';

class YourDocumentsService {
  private repository: BackofficeRepository = new BackofficeRepository();

  public retrieveDocumentTypes(): Promise<DocumentType[]> {
    return this.repository.get('/workdocs/file-types', { withCredentials: false })
      .then((response) => this.parseDocumentTypeList(response.data))
      .catch(() => []);
  }

  public retrieveRepFiles(): Promise<Document[]> {
    return this.repository.get('/workdocs/files', { withCredentials: false })
      .then((response) => this.parseDocumentList(response.data))
      .catch(() => []);
  }

  public retrieveDownloadMetadata(document: Document) {
    const uri = `/workdocs/download/${document.id}/${document.versionId}`;
    return this.repository.get(uri, { withCredentials: false })
      .then((response) => new DocumentMetadata(response.data.Metadata));
  }

  public generateUploadMetadata(document: Document): Promise<UploadMetadata> {
    const requestBody = {
      fileName: document.name,
      fileSize: document.size,
      contentType: document.mimeType,
      fileTypeId: document.type?.id ?? 0,
    };

    return this.repository.post('/workdocs/upload-metadata', requestBody)
      .then((response) => new UploadMetadata(response.data))
      .catch(() => {
        throw new Error('GENERAL_ERROR.SAVE_INFO');
      });
  }

  public uploadDocument(uploadMetadata: UploadMetadata, file: File) {
    const config = {
      headers: {
        'Content-Type': uploadMetadata.contentType,
        'x-amz-server-side-encryption': 'AES256',
      },
    };

    return this.repository.put(uploadMetadata.uploadUrl, file, config)
      .catch(() => {
        throw new Error('GENERAL_ERROR.SAVE_INFO');
      });
  }

  public activateDocumentVersion(uploadMetadata: UploadMetadata) {
    const requestBody = {
      documentId: uploadMetadata.documentId,
      versionId: uploadMetadata.versionId,
    };

    return this.repository.post('/workdocs/upload/confirm', requestBody)
      .catch(() => {
        throw new Error('GENERAL_ERROR.SAVE_INFO');
      });
  }

  public validateFile(document: Document, file: File): void {
    this.checkDocumentType(document);
    this.checkFileSize(file);
    this.checkFileExtensionValid(file);
  }

  private checkDocumentType(document: Document): void {
    if (document.type === null || document.type.id === null) {
      throw new Error('PROFILE.YOUR_DOCUMENTS.DOCUMENT_TYPE_REQUIRED');
    }
  }

  private checkFileExtensionValid(file: File): void {
    const fileExtension = file.name.substr((file.name.lastIndexOf('.') + 1));
    if (!/(doc|docx|pdf|jpg|jpeg|tiff|tif|png)$/ig.test(fileExtension)) {
      throw new Error('PROFILE.YOUR_DOCUMENTS.EXTENTION_NOT_SUPPORTED');
    }
  }

  private checkFileSize(file: File): void {
    const fileSize = file.size / 1000000;
    if (fileSize > 10) {
      throw new Error('PROFILE.YOUR_DOCUMENTS.FILE_TOO_LARGE');
    }
  }

  private parseDocumentTypeList(data: any): DocumentType[] {
    return data.map((documentType: any) => new DocumentType(documentType));
  }

  private parseDocumentList(data: any): Document[] {
    return data.documents.map((document: any) => {
      const documentType = new DocumentType({
        description: document.fileType,
      });

      const newDocument = new Document(document);
      newDocument.setDocumentType(documentType);
      return newDocument;
    });
  }
}

export default new YourDocumentsService();
